import { useMemo } from 'react';

import {
  Currencies,
  PaymentMethodType,
  PaymentOptions,
  PaymentPreferences,
} from 'src/services/api/invoicesApi';

/**
 * Derives what the checkout offers from the payment preferences saved on an
 * invoice, payment link or product store, and the currency it charges in.
 * Bank payments are USD-only, so they are hidden for any other currency.
 */
export const getPaymentOptions = (
  paymentPreferences: PaymentPreferences | undefined,
  currency: string | null | undefined,
): PaymentOptions => {
  const isUSD =
    !currency || currency.toUpperCase() === Currencies.USD.toString();

  const allowPaymentViaACH =
    isUSD &&
    Boolean(
      paymentPreferences?.paymentMethodTypes?.includes(
        PaymentMethodType.BankAccount,
      ),
    );

  return {
    allowPaymentViaACH,
    allowPaymentViaCC: Boolean(
      paymentPreferences?.paymentMethodTypes?.includes(
        PaymentMethodType.CreditCard,
      ),
    ),
    absorbACHFees: Boolean(
      paymentPreferences?.absorbTransactionFee?.includes(
        PaymentMethodType.BankAccount,
      ),
    ),
    absorbCreditCardFee: Boolean(
      paymentPreferences?.absorbTransactionFee?.includes(
        PaymentMethodType.CreditCard,
      ),
    ),
    allowPlaidSetup: allowPaymentViaACH,
  };
};

export const usePaymentOptions = (
  paymentPreferences: PaymentPreferences | undefined,
  currency: string | null | undefined,
): PaymentOptions =>
  useMemo(
    () => getPaymentOptions(paymentPreferences, currency),
    [paymentPreferences, currency],
  );
